'use client'

import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { toast } from '@/lib/toast'
import { useAuth } from '@/lib/commerce/hooks'
import { convertPointsApi } from '@/lib/commerce/services/loyalty-api'
import { formatNumber, formatPrice } from '@/utils/util'
import CenterModal from '../elements/CenterModal'
import { panelFocusRing } from '../elements/SidePanel'
import { TextField } from '../elements/Field'

const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'

export default function ConvertVoucherModal({ open, setOpen, vendor, program, balance, onConverted }: { open: boolean, setOpen: (open: boolean) => void, vendor: any, program: any, balance: number, onConverted: () => void }) {
  const { session } = useAuth()
  const [convertPoints, setConvertPoints] = useState('')
  const [converting, setConverting] = useState(false)

  const minPoints = program?.minPointsToConvert || 1
  const points = Number(convertPoints) || 0
  const convertValue = points * (program?.pointValue || 1)
  const isValid = points >= minPoints && points <= (balance || 0)

  const handleConvert = async () => {
    if (!points || points < minPoints) return toast.error(`Хамгийн багадаа ${formatNumber(minPoints)} оноо хөрвүүлнэ`)
    if (points > (balance || 0)) return toast.error('Онооны үлдэгдэл хүрэлцэхгүй байна')
    setConverting(true)
    const { data, error } = await convertPointsApi({ vendorId: vendor?.id, session: session || undefined }, points)
    setConverting(false)
    if (error) return toast.error(error)
    if (!data) return toast.error('Алдаа гарлаа. Дахин оролдоно уу')
    toast.success('Ваучер амжилттай үүслээ')
    setOpen(false)
    setConvertPoints('')
    onConverted()
  }

  return (
    <CenterModal
      open={open}
      onClose={() => setOpen(false)}
      title="Оноог ваучер болгох"
      subtitle={<p className="mt-2 text-sm text-muted-foreground">Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа ашиглаарай</p>}
      footer={
        <div className="flex justify-end">
          <button type="button" onClick={handleConvert} disabled={!isValid || converting} className={`h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-40 disabled:cursor-not-allowed ${panelFocusRing}`}>
            {converting ? 'Хөрвүүлж байна…' : 'Хөрвүүлэх'}
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-6 pb-6 border-b border-border">
        <div>
          <div className={eyebrow}>Онооны үлдэгдэл</div>
          <div className="mt-2 text-2xl font-light tabular-nums">{formatNumber(balance || 0)}</div>
        </div>
        <div className="text-right">
          <div className={eyebrow}>Ханш</div>
          <div className="mt-2 text-sm tabular-nums">1 оноо = {formatNumber(program?.pointValue || 1)}₮</div>
        </div>
      </div>

      <div className="mt-6">
        <TextField label="Хөрвүүлэх оноо" inputMode="numeric" value={convertPoints} onChange={e => setConvertPoints(e.target.value.replace(/[^0-9]/g, ''))} placeholder={String(program?.minPointsToConvert || 100)} description={`Хамгийн багадаа ${formatNumber(minPoints)} оноо`} />
        <div className="mt-3 flex gap-2">
          {[25, 50, 100].map((percent) => {
            const value = Math.floor(((balance || 0) * percent) / 100)
            const selected = points === value && value > 0
            return (
              <button key={percent} type="button" disabled={value < 1} onClick={() => setConvertPoints(String(value))} aria-pressed={selected} className={`h-8 px-3 border text-xs tabular-nums transition-colors duration-150 disabled:opacity-40 ${selected ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'} ${panelFocusRing}`}>
                {percent === 100 ? 'Бүгд' : `${percent}%`}
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-8 py-6 border-y border-border flex items-center justify-between gap-4">
        <div>
          <div className={eyebrow}>Оноо</div>
          <div className="mt-2 text-lg tabular-nums">{formatNumber(points)}</div>
        </div>
        <ArrowRight aria-hidden className="size-4 text-muted-foreground" strokeWidth={1.25} />
        <div className="text-right">
          <div className={eyebrow}>Ваучерын дүн</div>
          <div className="mt-2 text-2xl tabular-nums">{formatPrice(convertValue)}</div>
        </div>
      </div>

      <div className="mt-8 border-l border-foreground pl-4">
        <p className="text-[13px] text-foreground">Хөрвүүлэхээсээ өмнө анхаараарай</p>
        <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-muted-foreground list-disc pl-4">
          <li>Ваучер болгосон оноог буцааж оноо болгох боломжгүй.</li>
          {program?.voucherPartialUse ? (
            <li>Ваучерын ашиглаагүй үлдэгдэл хадгалагдаж, дараагийн захиалгад ашиглагдана.</li>
          ) : (
            <li>Ваучер нэг захиалгад бүтнээрээ зарцуулагдах ба захиалгын дүн ваучераас бага бол зөрүү буцаан олгогдохгүй.</li>
          )}
          {program?.voucherExpireDays ? <li>Үүссэн ваучер {program.voucherExpireDays} хоногийн дотор хүчинтэй.</li> : null}
        </ul>
      </div>
    </CenterModal>
  )
}
